// Utility functions for the Besalla Cashier application

import {
  getActivePhoneRegion,
  isValidPhoneForRegion,
} from './currencyUtils'

// Format currency based on settings
export const formatCurrency = (amount: number, currency: string = 'SAR'): string => {
  return new Intl.NumberFormat('ar-SA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

// Format date based on language
export const formatDate = (date: string | Date, language: 'ar' | 'en' = 'ar'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')
}

// Format time based on language and format
export const formatTime = (
  date: string | Date, 
  language: 'ar' | 'en' = 'ar', 
  format: '12h' | '24h' = '24h'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleTimeString(
    language === 'ar' ? 'ar-SA' : 'en-US',
    { 
      hour12: format === '12h',
      hour: '2-digit',
      minute: '2-digit'
    }
  )
}

// Calculate tax amount
export const calculateTax = (amount: number, taxRate: number = 0.15): number => {
  return Math.round(amount * taxRate * 100) / 100
}

// Calculate total with tax
export const calculateTotalWithTax = (amount: number, taxRate: number = 0.15): number => {
  return amount + calculateTax(amount, taxRate)
}

// Service fee returned by the API as `service_price`; 0 when missing or not numeric
export const parseServicePrice = (source: any): number => {
  const raw = source?.service_price ?? source?.servicePrice
  if (raw == null || raw === '') return 0
  const value = typeof raw === 'number' ? raw : parseFloat(String(raw))
  return Number.isFinite(value) && value > 0 ? value : 0
}

// Service fee percentage returned by the API as `service_fee_percentage`; null when missing or not numeric
export const parseServiceFeePercentage = (source: any): number | null => {
  const raw = source?.service_fee_percentage ?? source?.serviceFeePercentage
  if (raw == null || raw === '') return null
  const value = typeof raw === 'number' ? raw : parseFloat(String(raw))
  return Number.isFinite(value) ? value : null
}

// Validate email
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Validate phone number (Settings-driven EG / existing SA)
export const isValidPhone = (phone: string): boolean => {
  const region = getActivePhoneRegion()
  const cleaned = phone.replace(/\s/g, '')

  if (region === 'EG') {
    return isValidPhoneForRegion(cleaned, 'EG')
  }

  // Existing Saudi format (unchanged)
  const phoneRegex = /^(\+966|966|0)?[5-9][0-9]{8}$/
  return phoneRegex.test(cleaned)
}

// Validate employee ID (5 digits)
export const isValidEmployeeId = (id: string): boolean => {
  return /^\d{5}$/.test(id)
}

// Validate PIN (4 digits)
export const isValidPin = (pin: string): boolean => {
  return /^\d{4}$/.test(pin)
}

// Generate order number
export const generateOrderNumber = (): string => {
  const now = new Date()
  const year = now.getFullYear().toString().slice(-2)
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')
  const time = now.getTime().toString().slice(-6)
  return `ORD${year}${month}${day}${time}`
}

// Debounce function
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Throttle function
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Deep clone object
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T
  if (typeof obj === 'object') {
    const clonedObj = {} as T
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
  return obj
}

// Local storage helpers
export const storage = {
  get: <T>(key: string, defaultValue?: T): T | null => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue || null
    } catch {
      return defaultValue || null
    }
  },
  
  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
    }
  },
  
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
    }
  },
  
  clear: (): void => {
    try {
      localStorage.clear()
    } catch (error) {
    }
  }
}

// API error handler
export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  if (error.message) {
    return error.message
  }
  return 'An unexpected error occurred'
}

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Generate random ID
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9)
}

// Check if running on mobile
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

// Check if running on tablet
export const isTablet = (): boolean => {
  return /iPad|Android(?=.*Mobile)/i.test(navigator.userAgent)
}

// Get device type
export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (isMobile()) return 'mobile'
  if (isTablet()) return 'tablet'
  return 'desktop'
}

// Sleep function for delays
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Retry function with exponential backoff
export const retry = async <T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    if (retries > 0) {
      await sleep(delay)
      return retry(fn, retries - 1, delay * 2)
    }
    throw error
  }
}

// Class name utility (similar to clsx)
export const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ')
}

// Convert numbers to Arabic numerals
export const convertToArabicNumbers = (num: number | string): string => {
  const numStr = typeof num === 'number' ? num.toString() : num
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
  return numStr.replace(/\d/g, (digit) => arabicNumerals[parseInt(digit)])
}

// Format number with English/Western numerals (always uses 0-9, not Arabic numerals)
// This ensures numbers are displayed in English format regardless of browser locale
export const formatNumberEnglish = (
  number: number | string | undefined | null,
  options?: {
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    useGrouping?: boolean
  }
): string => {
  if (number === undefined || number === null) return '0'
  
  let value: number
  if (typeof number === 'string') {
    value = parseFloat(number) || 0
  } else {
    value = number
  }
  
  // Use 'en-US' locale to ensure English numerals (0-9) are always used
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    useGrouping: options?.useGrouping ?? true,
  }).format(value)
}